import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { authAPI } from '../services/api';
import { setUser as setSentryUser } from '../config/sentry';

const AuthContext = createContext(null);

// ─── Constants ───────────────────────────────────────────────────────────────
const SESSION_TIMEOUT_MS = 30 * 60 * 1000;   // 30 min inactivity → logout
const TOKEN_REFRESH_INTERVAL = 10 * 60 * 1000; // Check token health every 10 min
const LOGIN_RATE_LIMIT = { maxAttempts: 5, windowMs: 5 * 60 * 1000 }; // 5 attempts / 5 min
const ACTIVITY_EVENTS = ['mousedown', 'keydown', 'touchstart', 'scroll'];
const LAST_ACTIVITY_KEY = 'last_activity';

// ─── Helpers ─────────────────────────────────────────────────────────────────
const parseJwtExp = (token) => {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.exp ? payload.exp * 1000 : null;
  } catch { return null; }
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [permissions, setPermissions] = useState([]);
  const [sessionStatus, setSessionStatus] = useState('active'); // active | expiring | expired
  const lastActivityRef = useRef(parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10) || Date.now());
  const refreshTimerRef = useRef(null);
  const activityTimerRef = useRef(null);
  const loginAttemptsRef = useRef([]);

  const logout = useCallback(() => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('user');
    localStorage.removeItem(LAST_ACTIVITY_KEY);
    setUser(null);
    setPermissions([]);
    setSessionStatus('expired');
    setSentryUser(null);
    clearInterval(refreshTimerRef.current);
    clearInterval(activityTimerRef.current);
  }, []);

  // ── Multi-tab auth sync via storage events ──
  useEffect(() => {
    const handleStorage = (e) => {
      if (e.key === 'access_token') {
        if (!e.newValue) {
          // Another tab logged out
          setUser(null);
          setPermissions([]);
          setSessionStatus('expired');
        } else if (!user && e.newValue) {
          // Another tab logged in → reload to pick up user
          const storedUser = localStorage.getItem('user');
          if (storedUser) {
            try {
              const parsed = JSON.parse(storedUser);
              setUser(parsed);
              setPermissions(parsed.permissions || parsed.role?.permissions || []);
              setSentryUser(parsed);
            } catch { /* ignore */ }
          }
        }
      }
    };
    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, [user]);

  // ── Token health monitor ──
  const checkTokenHealth = useCallback(() => {
    const token = localStorage.getItem('access_token');
    if (!token) { setSessionStatus('expired'); return; }

    const exp = parseJwtExp(token);
    if (!exp) return;

    const remaining = exp - Date.now();
    if (remaining <= 0) {
      // Token expired — attempt refresh via API call (the 401 interceptor will try refresh token)
      const refreshToken = localStorage.getItem('refresh_token');
      if (refreshToken) {
        setSessionStatus('expiring');
        authAPI.getMe()
          .then((res) => {
            if (res.data) {
              setUser(res.data);
              localStorage.setItem('user', JSON.stringify(res.data));
              setSentryUser(res.data);
              setSessionStatus('active');
            }
          })
          .catch(() => {
            setSessionStatus('expired');
            logout();
          });
      } else {
        setSessionStatus('expired');
        logout();
      }
    } else if (remaining < 5 * 60 * 1000) {
      // Less than 5 min → try refresh (but don't trigger re-render loop)
      if (sessionStatus === 'expiring') return; // Already refreshing
      setSessionStatus('expiring');
      authAPI.getMe()
        .then((res) => {
          if (res.data) {
            // Only update user if data actually changed (prevent re-render cascade)
            setUser(prev => {
              const newId = res.data.id || res.data._id;
              const prevId = prev?.id || prev?._id;
              if (newId === prevId && prev) {
                // Same user — merge without new reference
                return prev;
              }
              return res.data;
            });
            localStorage.setItem('user', JSON.stringify(res.data));
            setSentryUser(res.data);
            setSessionStatus('active');
          }
        })
        .catch(() => {
          setSessionStatus('expired');
          logout();
        });
    } else {
      setSessionStatus('active');
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Start/stop token health timer
  useEffect(() => {
    if (user) {
      refreshTimerRef.current = setInterval(checkTokenHealth, TOKEN_REFRESH_INTERVAL);
      checkTokenHealth(); // immediate check
    }
    return () => clearInterval(refreshTimerRef.current);
  }, [user, checkTokenHealth]);

  // ── Inactivity timeout ──
  const touchActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    localStorage.setItem(LAST_ACTIVITY_KEY, String(lastActivityRef.current));
  }, []);

  useEffect(() => {
    if (!user) return;

    // Attach activity listeners
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, touchActivity, { passive: true }));

    // Check inactivity periodically
    activityTimerRef.current = setInterval(() => {
      if (Date.now() - lastActivityRef.current > SESSION_TIMEOUT_MS) {
        setSessionStatus('expired');
        logout();
        window.dispatchEvent(new CustomEvent('session-expired', {
          detail: { message: 'Session timed out due to inactivity.', redirectTo: '/login' },
        }));
      }
    }, 60_000); // check every 1 min

    // Mobile fix: check inactivity when tab becomes visible again
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        if (Date.now() - lastActivityRef.current > SESSION_TIMEOUT_MS) {
          setSessionStatus('expired');
          logout();
          window.dispatchEvent(new CustomEvent('session-expired', {
            detail: { message: 'Session timed out due to inactivity.', redirectTo: '/login' },
          }));
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, touchActivity));
      clearInterval(activityTimerRef.current);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [user, touchActivity, logout]);

  // ── Initial bootstrap ──
  useEffect(() => {
    const token = localStorage.getItem('access_token');
    const refreshToken = localStorage.getItem('refresh_token');
    const storedUser = localStorage.getItem('user');

    // Enforce inactivity timeout across app restarts / mobile tab reopens
    const lastActivity = parseInt(localStorage.getItem(LAST_ACTIVITY_KEY), 10);
    if (lastActivity && (Date.now() - lastActivity > SESSION_TIMEOUT_MS)) {
      logout();
      setLoading(false);
      return;
    }

    if ((token || refreshToken) && storedUser) {
      try {
        const parsedUser = JSON.parse(storedUser);
        setUser(parsedUser);
        setPermissions(parsedUser.permissions || parsedUser.role?.permissions || []);
        setSentryUser(parsedUser);
      } catch { /* malformed JSON */ }

      authAPI.getMe()
        .then(res => {
          setUser(res.data);
          setPermissions(res.data.permissions || res.data.role?.permissions || []);
          localStorage.setItem('user', JSON.stringify(res.data));
          setSentryUser(res.data);
        })
        .catch(() => {
          logout();
        })
        .finally(() => setLoading(false));
    } else {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ── Login rate limiting ──
  const checkLoginRateLimit = () => {
    const now = Date.now();
    loginAttemptsRef.current = loginAttemptsRef.current.filter(
      (t) => now - t < LOGIN_RATE_LIMIT.windowMs
    );
    if (loginAttemptsRef.current.length >= LOGIN_RATE_LIMIT.maxAttempts) {
      const oldestInWindow = loginAttemptsRef.current[0];
      const waitSec = Math.ceil((LOGIN_RATE_LIMIT.windowMs - (now - oldestInWindow)) / 1000);
      throw new Error(`Too many login attempts. Please wait ${waitSec}s.`);
    }
    loginAttemptsRef.current.push(now);
  };

  const login = async (email, password) => {
    checkLoginRateLimit();
    touchActivity();

    const response = await authAPI.login({ email, password });

    if (response.data.requiresTwoFactor) {
      return { requiresTwoFactor: true, userId: response.data.userId };
    }

    const { user: userData, accessToken, refreshToken } = response.data;
    if (!accessToken) throw new Error('Login succeeded but no token returned from server');

    localStorage.setItem('access_token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setUser(userData);
    setPermissions(userData.permissions || userData.role?.permissions || []);
    setSessionStatus('active');
    setLoading(false);
    setSentryUser(userData);

    return userData;
  };

  const register = async (name, email, password) => {
    touchActivity();
    const response = await authAPI.register({ name, email, password });
    const { user: userData, accessToken, refreshToken } = response.data;

    localStorage.setItem('access_token', accessToken);
    if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
    localStorage.setItem('user', JSON.stringify(userData));
    localStorage.setItem(LAST_ACTIVITY_KEY, String(Date.now()));
    setUser(userData);
    setPermissions(userData.permissions || userData.role?.permissions || []);
    setSessionStatus('active');
    setLoading(false);
    setSentryUser(userData);

    return userData;
  };

  const updateUser = (updatedUser) => {
    setUser(updatedUser);
    setPermissions(updatedUser.permissions || updatedUser.role?.permissions || []);
    localStorage.setItem('user', JSON.stringify(updatedUser));
    setSentryUser(updatedUser);
  };

  // ── Permission helpers ──
  /**
   * Check if user has a specific permission
   * @param {string} permission — e.g. 'products:read'
   */
  const hasPermission = useCallback(
    (permission) => {
      if (!user) return false;
      // System admin bypasses all checks
      if (permissions.includes('system:admin')) return true;
      return permissions.includes(permission);
    },
    [user, permissions]
  );

  /**
   * Check if user has ANY of the given permissions
   * @param {string[]} perms
   */
  const hasAnyPermission = useCallback(
    (perms) => perms.some((p) => hasPermission(p)),
    [hasPermission]
  );

  /**
   * Check if user has ALL of the given permissions
   * @param {string[]} perms
   */
  const hasAllPermissions = useCallback(
    (perms) => perms.every((p) => hasPermission(p)),
    [hasPermission]
  );

  /** User display name (first name or email prefix) */
  const displayName = user?.name?.split(' ')[0] || user?.email?.split('@')[0] || '';

  /** User initials (for avatar fallback) */
  const initials = user?.name
    ? user.name.split(' ').slice(0, 2).map((w) => w[0]).join('').toUpperCase()
    : (user?.email?.[0] || '?').toUpperCase();

  const value = {
    user,
    loading,
    login,
    register,
    logout,
    updateUser,
    isAuthenticated: !!user,
    // New: permission system
    permissions,
    hasPermission,
    hasAnyPermission,
    hasAllPermissions,
    // New: session management
    sessionStatus,
    touchActivity,
    // New: computed helpers
    displayName,
    initials,
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-brand-50 to-brand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-4 border-brand-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg font-semibold">Loading Gabhan ERP...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// eslint-disable-next-line react-refresh/only-export-components
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};
